import React, { useEffect, useMemo, useState } from "react";
import Multiselect from "multiselect-react-dropdown";
import { toast } from "react-toastify";

import SimpleDialog from "../../../common/simpleDialog";
import { simulateAssignment, applyAssignment, getAdvisorSegments, getAdvisorAssignmentCriteria } from "./apis";
import "./SimulateAssignment.css";

// Mirrors the server-side zip normalization (strip non-digits, keep first 5).
const normalizeZipCode = (value) => {
    const digits = (value || "").replace(/\D/g, "");
    return digits.length >= 5 ? digits.substring(0, 5) : "";
};

// OWL-2293: read-only assignment simulator. Lets a director/admin tweak a hypothetical rule set for an
// advisor and see the projected caseload (new count, gained, lost) before anything is committed. "Apply"
// commits the rule set through the dedicated, Admin-only ApplyAssignment endpoint, which reconciles the
// advisor's saved criteria server-side. Apply is only rendered for edit-capable (Admin) users; directors
// can run and view but not apply. Segments already attached to the advisor are carried through unchanged.
//
// Two ways to seed the hypothetical rule set on open:
//   - initial* props: the admin advisor form already has the advisor's selections loaded and passes them in.
//   - loadCurrentCriteria: when the host doesn't have the criteria loaded (e.g. the supervisor's team view),
//     the modal fetches the advisor's persisted criteria itself and maps them against the provided options.
const SimulateAssignmentModal = ({
    open,
    onClose,
    userProfileId,
    advisorChoices,
    schoolOptions,
    specialGroupOptions,
    gradeOptions,
    initialSchools,
    initialGrades,
    initialZipCodes,
    initialSpecialGroups,
    loadCurrentCriteria,
    canApply,
    onApplied,
}) => {
    const [schools, setSchools] = useState([]);
    const [grades, setGrades] = useState([]);
    const [zipCodes, setZipCodes] = useState([]);
    const [specialGroups, setSpecialGroups] = useState([]);
    const [zipInput, setZipInput] = useState("");
    const [segmentIds, setSegmentIds] = useState([]);
    const [result, setResult] = useState(null);
    const [running, setRunning] = useState(false);
    const [applying, setApplying] = useState(false);
    const [selectedAdvisorId, setSelectedAdvisorId] = useState("");

    // When the host provides a list of advisors (supervisor team view), the modal lets the user pick which
    // direct report to simulate. Otherwise it targets the fixed userProfileId passed in.
    const hasChoices = Array.isArray(advisorChoices) && advisorChoices.length > 0;
    const activeUserProfileId = hasChoices ? selectedAdvisorId : userProfileId;

    // Default the picker to the first direct report each time the modal opens.
    useEffect(() => {
        if (!open || !hasChoices) {
            return;
        }
        const ids = advisorChoices.map((c) => c.id);
        setSelectedAdvisorId((prev) =>
            prev && ids.includes(prev)
                ? prev
                : ids.includes(userProfileId)
                ? userProfileId
                : advisorChoices[0].id
        );
    }, [open, hasChoices, advisorChoices, userProfileId]);

    // Reset the hypothetical rule set from the advisor's current selections each time the modal opens or the
    // selected advisor changes.
    useEffect(() => {
        if (!open) {
            return;
        }
        setZipInput("");
        setResult(null);

        if (!activeUserProfileId) {
            // No advisor picked yet (team view still initializing): show an empty rule set.
            setSchools([]);
            setGrades([]);
            setZipCodes([]);
            setSpecialGroups([]);
            setSegmentIds([]);
            return;
        }

        if (loadCurrentCriteria) {
            // Host doesn't have the criteria loaded (supervisor team view): fetch them and map the returned
            // ids to the option objects so every criterion (schools/grades/zips/special groups) is seeded
            // accurately. Segments are carried through via the returned ids.
            setSchools([]);
            setGrades([]);
            setZipCodes([]);
            setSpecialGroups([]);
            setSegmentIds([]);
            getAdvisorAssignmentCriteria(activeUserProfileId)
                .then((resp) => {
                    const c = resp.data.result || {};
                    const schoolIds = c.schoolIds || [];
                    const gradeValues = c.grades || [];
                    const groupIds = c.specialGroupIds || [];
                    setSchools((schoolOptions || []).filter((o) => schoolIds.includes(o.id)));
                    setGrades((gradeOptions || []).filter((o) => gradeValues.includes(o.grade)));
                    setZipCodes(c.zipCodes || []);
                    setSpecialGroups((specialGroupOptions || []).filter((o) => groupIds.includes(o.id)));
                    setSegmentIds(c.segmentIds || []);
                })
                .catch(() => {
                    toast.error("Could not load the coach's current assignment criteria.");
                });
            return;
        }

        setSchools(initialSchools || []);
        setGrades(initialGrades || []);
        setZipCodes(initialZipCodes || []);
        setSpecialGroups(initialSpecialGroups || []);

        // Segments have no editor on the advisor form yet; load the advisor's attached segments so they are
        // carried through the projection unchanged. Best-effort: if the caller lacks segment permission the
        // simulation simply runs without segment carry-over.
        getAdvisorSegments(activeUserProfileId)
            .then((resp) => setSegmentIds((resp.data.result || []).map((s) => s.segmentId)))
            .catch(() => setSegmentIds([]));
    }, [open, activeUserProfileId, loadCurrentCriteria, initialSchools, initialGrades, initialZipCodes, initialSpecialGroups, schoolOptions, gradeOptions, specialGroupOptions]);

    const buildCriteria = () => ({
        userProfileId: activeUserProfileId,
        schoolIds: schools.map((s) => s.id),
        grades: grades.map((g) => g.grade),
        zipCodes,
        specialGroupIds: specialGroups.map((g) => g.id),
        segmentIds,
    });

    const onAddZip = () => {
        const zip = normalizeZipCode(zipInput);
        if (!zip || zipCodes.includes(zip)) {
            setZipInput("");
            return;
        }
        setZipCodes([...zipCodes, zip]);
        setZipInput("");
    };

    const onZipKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            onAddZip();
        }
    };

    const onRun = async () => {
        setRunning(true);
        try {
            const resp = await simulateAssignment(buildCriteria());
            setResult(resp.data.result);
        } catch (err) {
            toast.error("Could not run the simulation. Please try again.");
        } finally {
            setRunning(false);
        }
    };

    const onApply = async () => {
        setApplying(true);
        try {
            // The server reconciles the advisor's persisted criteria to this rule set (Admin-only endpoint).
            await applyAssignment(buildCriteria());
            toast.success("Assignment changes applied.");
            if (onApplied) {
                await onApplied();
            }
            onClose();
        } catch (err) {
            toast.error("Could not apply the changes. Please review and try again.");
        } finally {
            setApplying(false);
        }
    };

    const busy = running || applying;

    const netClass = useMemo(() => {
        if (!result) {
            return "";
        }
        const net = result.newCaseloadCount - result.currentCaseloadCount;
        if (net > 0) return "simAssign-net simAssign-net--up";
        if (net < 0) return "simAssign-net simAssign-net--down";
        return "simAssign-net";
    }, [result]);

    const renderStudentList = (title, students, variant) => (
        <div className={`simAssign-listBlock simAssign-listBlock--${variant}`}>
            <div className="simAssign-listHeader">
                {title} ({students.length})
            </div>
            {students.length === 0 ? (
                <div className="simAssign-empty">None</div>
            ) : (
                <ul className="simAssign-list">
                    {students.map((s) => (
                        <li key={s.userProfileId}>
                            {s.name}
                            {s.schoolName ? ` — ${s.schoolName}` : ""}
                            {s.currentGrade ? ` (Gr ${s.currentGrade})` : ""}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );

    return (
        <SimpleDialog
            open={open}
            onClose={onClose}
            title="Simulate Assignment"
            loading={busy}
            className="simAssignDialog"
            maxWidth="md"
        >
            <div className="simAssign-body">
                <p className="simAssign-help">
                    Adjust the hypothetical rule set below and run the simulation to preview the caseload change.
                    Nothing is saved until you choose <strong>Apply</strong>.
                </p>

                {hasChoices && (
                    <div className="simAssign-field">
                        <span>Coach</span>
                        <select
                            className="simAssign-advisorSelect"
                            value={selectedAdvisorId}
                            onChange={(e) => setSelectedAdvisorId(e.target.value)}
                            disabled={busy}
                        >
                            {advisorChoices.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="simAssign-field">
                    <span>Schools</span>
                    <Multiselect
                        options={schoolOptions}
                        selectedValues={schools}
                        onSelect={(list) => setSchools(list)}
                        onRemove={(list) => setSchools(list)}
                        displayValue="name"
                        disable={busy}
                    />
                </div>

                <div className="simAssign-field">
                    <span>Grades</span>
                    <Multiselect
                        options={gradeOptions}
                        selectedValues={grades}
                        onSelect={(list) => setGrades(list)}
                        onRemove={(list) => setGrades(list)}
                        displayValue="name"
                        disable={busy}
                    />
                </div>

                <div className="simAssign-field">
                    <span>Zip Codes</span>
                    <div className="simAssign-zipChips">
                        {zipCodes.map((zip) => (
                            <span className="simAssign-zipChip" key={zip}>
                                {zip}
                                <button
                                    type="button"
                                    onClick={() => setZipCodes(zipCodes.filter((z) => z !== zip))}
                                    disabled={busy}
                                    aria-label={`Remove zip code ${zip}`}
                                >
                                    &times;
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="simAssign-zipRow">
                        <input
                            type="text"
                            value={zipInput}
                            onChange={(e) => setZipInput(e.target.value)}
                            onKeyDown={onZipKeyDown}
                            placeholder="Add zip code (e.g. 02903)"
                            maxLength={10}
                            disabled={busy}
                        />
                        <button type="button" onClick={onAddZip} disabled={busy}>
                            Add
                        </button>
                    </div>
                </div>

                <div className="simAssign-field">
                    <span>Special Groups</span>
                    <Multiselect
                        options={specialGroupOptions}
                        selectedValues={specialGroups}
                        onSelect={(list) => setSpecialGroups(list)}
                        onRemove={(list) => setSpecialGroups(list)}
                        displayValue="name"
                        disable={busy}
                    />
                </div>

                <div className="simAssign-actions">
                    <button type="button" className="simAssign-run" onClick={onRun} disabled={busy || !activeUserProfileId}>
                        {running ? "Running..." : "Run Simulation"}
                    </button>
                </div>

                {result && (
                    <div className="simAssign-results">
                        <div className="simAssign-summary">
                            <div className="simAssign-stat">
                                <div className="simAssign-statValue">{result.currentCaseloadCount}</div>
                                <div className="simAssign-statLabel">Current</div>
                            </div>
                            <div className="simAssign-stat">
                                <div className="simAssign-statValue">{result.newCaseloadCount}</div>
                                <div className="simAssign-statLabel">Projected</div>
                            </div>
                            <div className="simAssign-stat">
                                <div className={netClass}>
                                    {result.newCaseloadCount - result.currentCaseloadCount > 0 ? "+" : ""}
                                    {result.newCaseloadCount - result.currentCaseloadCount}
                                </div>
                                <div className="simAssign-statLabel">Net change</div>
                            </div>
                            <div className="simAssign-stat">
                                <div className="simAssign-statValue simAssign-gainedValue">+{result.gainedCount}</div>
                                <div className="simAssign-statLabel">Gained</div>
                            </div>
                            <div className="simAssign-stat">
                                <div className="simAssign-statValue simAssign-lostValue">-{result.lostCount}</div>
                                <div className="simAssign-statLabel">Lost</div>
                            </div>
                        </div>

                        <div className="simAssign-lists">
                            {renderStudentList("Students Gained", result.gained, "gained")}
                            {renderStudentList("Students Lost", result.lost, "lost")}
                        </div>

                        {/* OWL-2294: coverage impact of the proposed change relative to the rest of the advisor
                            population — which gained students were previously uncovered (gaps closed) and which
                            are already owned by another advisor (new overlaps). */}
                        <div className="simAssign-coverage">
                            <div className="simAssign-coverageHeader">Coverage Impact</div>
                            <div className="simAssign-summary">
                                <div className="simAssign-stat">
                                    <div className="simAssign-statValue simAssign-gainedValue">{result.gapsResolvedCount}</div>
                                    <div className="simAssign-statLabel">Gaps Resolved</div>
                                </div>
                                <div className="simAssign-stat">
                                    <div className="simAssign-statValue simAssign-lostValue">{result.newOverlapsCount}</div>
                                    <div className="simAssign-statLabel">New Overlaps</div>
                                </div>
                            </div>
                            <div className="simAssign-lists">
                                {renderStudentList("Gaps Resolved", result.gapsResolved || [], "gained")}
                                <div className="simAssign-listBlock simAssign-listBlock--lost">
                                    <div className="simAssign-listHeader">
                                        New Overlaps ({(result.newOverlaps || []).length})
                                    </div>
                                    {(result.newOverlaps || []).length === 0 ? (
                                        <div className="simAssign-empty">None</div>
                                    ) : (
                                        <ul className="simAssign-list">
                                            {(result.newOverlaps || []).map((s) => (
                                                <li key={s.userProfileId}>
                                                    {s.name}
                                                    {s.schoolName ? ` — ${s.schoolName}` : ""}
                                                    {s.currentGrade ? ` (Gr ${s.currentGrade})` : ""}
                                                    {s.conflictingAdvisors && s.conflictingAdvisors.length > 0 ? (
                                                        <span className="simAssign-conflict">
                                                            {" "}also: {s.conflictingAdvisors.join(", ")}
                                                        </span>
                                                    ) : null}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="simAssign-footer">
                    <button type="button" className="simAssign-cancel" onClick={onClose} disabled={busy}>
                        Close
                    </button>
                    {canApply && (
                        <button
                            type="button"
                            className="simAssign-apply"
                            onClick={onApply}
                            disabled={busy || !result}
                            title={!result ? "Run a simulation first" : "Commit these criteria to the coach"}
                        >
                            {applying ? "Applying..." : "Apply"}
                        </button>
                    )}
                </div>
            </div>
        </SimpleDialog>
    );
};

export default SimulateAssignmentModal;
